import "./styles/adjacent.css";
import { useState, useEffect, useLayoutEffect, lazy, Suspense, type ReactNode } from "react";
import { LazyMotion, useReducedMotion } from "motion/react";
import { initializeSEO } from "./utils/seo";
import { pathToPageId, pageIdToPath, navigateTo, navigateToHash, navigateToArticle, navigateToEdition, redirectLegacyHash, consumeScrollReset } from "./utils/routes";
import { scrollToSection } from "./utils/scroll";
import { PageTransition } from "./components/PageTransition";
import { ErrorBoundary } from "./components/ErrorBoundary";
import type { PageId } from "./types";

const AdvisoryPage = lazy(() =>
  import("./components/refresh/AdvisoryPages").then((m) => ({ default: m.AdvisoryPage }))
);
const PendiumHomePage = lazy(() =>
  import("./pages/PendiumHomePage").then((m) => ({ default: m.PendiumHomePage }))
);
const FirstHomeBuyerPage = lazy(() =>
  import("./pages/FirstHomeBuyerPage").then((m) => ({ default: m.FirstHomeBuyerPage }))
);
const DownsizingPage = lazy(() =>
  import("./pages/DownsizingPage").then((m) => ({ default: m.DownsizingPage }))
);
const BlogPage = lazy(() =>
  import("./pages/BlogPage").then((m) => ({ default: m.BlogPage }))
);
const ArticlePage = lazy(() =>
  import("./pages/ArticlePage").then((m) => ({ default: m.ArticlePage }))
);
const RentRollAcquisitionPage = lazy(() =>
  import("./pages/RentRollAcquisitionPage").then((m) => ({ default: m.RentRollAcquisitionPage }))
);
const NewsletterArchivePage = lazy(() =>
  import("./pages/NewsletterArchivePage").then((m) => ({ default: m.NewsletterArchivePage }))
);
const NewsletterEditionPage = lazy(() =>
  import("./pages/NewsletterEditionPage").then((m) => ({ default: m.NewsletterEditionPage }))
);


const FeedbackComplaintsPage = lazy(() =>
  import("./pages/FeedbackComplaintsPage").then((m) => ({ default: m.FeedbackComplaintsPage }))
);
const WebsitePolicyPage = lazy(() =>
  import("./pages/WebsitePolicyPage").then((m) => ({ default: m.WebsitePolicyPage }))
);
const NotFoundPage = lazy(() =>
  import("./pages/NotFoundPage").then((m) => ({ default: m.NotFoundPage }))
);

/**
 * Route chunks by page id, for prefetching on intent. Vite resolves a dynamic
 * import of the same specifier to the same chunk, so these are the same modules
 * the lazy() calls above load; calling one early only warms the cache. A visitor
 * who hovers or focuses a link for the usual 200–400ms before clicking then
 * arrives on a warm route: no fallback, and the enter plays on real content.
 */
const ROUTE_LOADERS: Partial<Record<PageId, () => Promise<unknown>>> = {
  finance: () => import("./components/refresh/AdvisoryPages"),
  services: () => import("./components/refresh/AdvisoryPages"),
  advisoryrentroll: () => import("./components/refresh/AdvisoryPages"),
  commercial: () => import("./components/refresh/AdvisoryPages"),
  property: () => import("./components/refresh/AdvisoryPages"),
  insights: () => import("./components/refresh/AdvisoryPages"),
  contact: () => import("./components/refresh/AdvisoryPages"),
  about: () => import("./components/refresh/AdvisoryPages"),
  local: () => import("./components/refresh/AdvisoryPages"),
  rentrollfinance: () => import("./components/refresh/AdvisoryPages"),
  tools: () => import("./components/refresh/AdvisoryPages"),
  home: () => import("./pages/PendiumHomePage"),
  firsthome: () => import("./pages/FirstHomeBuyerPage"),
  downsizing: () => import("./pages/DownsizingPage"),
  blog: () => import("./pages/BlogPage"),
  article: () => import("./pages/ArticlePage"),
  rentroll: () => import("./pages/RentRollAcquisitionPage"),
  newsletter: () => import("./pages/NewsletterArchivePage"),
  edition: () => import("./pages/NewsletterEditionPage"),
  complaints: () => import("./pages/FeedbackComplaintsPage"),
  websitepolicy: () => import("./pages/WebsitePolicyPage"),
};
const prefetched = new Set<PageId>();
function prefetchPage(page: PageId) {
  const load = ROUTE_LOADERS[page];
  if (!load || prefetched.has(page)) return;
  prefetched.add(page);
  load().catch(() => prefetched.delete(page));
}
function prefetchRouteFor(anchor: HTMLAnchorElement) {
  if (anchor.target || anchor.hasAttribute("download")) return;
  const connection = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection;
  if (connection?.saveData) return;
  let url: URL;
  try { url = new URL(anchor.href, window.location.href); } catch { return; }
  if (url.origin !== window.location.origin) return;
  prefetchPage(pathToPageId(url.pathname));
}
function useRoutePrefetch() {
  useEffect(() => {
    const onIntent = (event: Event) => {
      const anchor = (event.target as Element | null)?.closest?.("a[href]");
      if (anchor) prefetchRouteFor(anchor as HTMLAnchorElement);
    };
    document.addEventListener("pointerover", onIntent, { passive: true });
    document.addEventListener("touchstart", onIntent, { passive: true });
    document.addEventListener("focusin", onIntent);
    return () => {
      document.removeEventListener("pointerover", onIntent);
      document.removeEventListener("touchstart", onIntent);
      document.removeEventListener("focusin", onIntent);
    };
  }, []);
}

/**
 * Every same-origin link to a known route is a client-side navigation,
 * including links to a section on another page. This used to live as an onClick on the advisory
 * page wrapper only, so the consumer header (/home, /first-home-buyer,
 * /downsizing) and every insights → article link were full document reloads:
 * the crossfade, the prefetch and the scroll discipline stopped at the seam
 * between the two surfaces. A React handler that has already called
 * preventDefault (the blog index's article cards) is left alone.
 */
function useRouteLinks() {
  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const anchor = (event.target as Element).closest("a");
      if (!anchor || anchor.target || anchor.hasAttribute("download")) return;
      const url = new URL(anchor.href, window.location.href);
      if (url.origin !== window.location.origin || url.search) return;
      const same = url.pathname === window.location.pathname;
      if (url.hash) {
        if (same) return; // useInPageAnchors handles the current page.
        const page = pathToPageId(url.pathname);
        if (page === "notfound" || pageIdToPath(page) !== url.pathname) return;
        event.preventDefault();
        navigateToHash(page, url.hash);
        return;
      }
      const article = url.pathname.match(/^\/blog\/([^/]+)$/);
      if (article) { event.preventDefault(); if (!same) navigateToArticle(decodeURIComponent(article[1])); return; }
      const edition = url.pathname.match(/^\/intelligence\/([^/]+)$/);
      if (edition) { event.preventDefault(); if (!same) navigateToEdition(decodeURIComponent(edition[1])); return; }
      const page = pathToPageId(url.pathname);
      if (page === "notfound" || pageIdToPath(page) !== url.pathname) return;
      event.preventDefault();
      if (!same) navigateTo(page);
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);
}

/**
 * Same-page anchors ("Explore our expertise ↓", "Explore the numbers", #team) scroll
 * rather than teleport. The browser's own anchor jump is instant and lands before
 * hashchange fires, so scrollToSection's smooth scroll had nothing left to do; here
 * the jump is intercepted, the hash is pushed, and the same hashchange path every
 * listener already uses (scrollToSection, DeferMount) runs from the current position.
 * scrollToSection honours prefers-reduced-motion, so that path stays instant.
 */
function useInPageAnchors() {
  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const anchor = (event.target as Element).closest("a");
      if (!anchor || anchor.target || anchor.hasAttribute("download")) return;
      const url = new URL(anchor.href, window.location.href);
      if (url.origin !== window.location.origin || url.pathname !== window.location.pathname || url.search !== window.location.search || url.hash.length < 2) return;
      event.preventDefault();
      if (url.hash !== window.location.hash) window.history.pushState(null, "", url.pathname + url.search + url.hash);
      window.dispatchEvent(new HashChangeEvent("hashchange"));
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);
}

/**
 * The scroll reset owed by an in-app navigation (see navigateTo in utils/routes)
 * runs in the layout effect of whatever mounts first for the new route, the
 * loading fallback or the content, so the new page is never painted at the old
 * scroll position. A real back/forward press owes no reset and is left alone.
 */
function useScrollResetOnMount() {
  useLayoutEffect(() => {
    if (consumeScrollReset()) window.scrollTo(0, 0);
  }, []);
}

/**
 * The route's ENTER. This sits inside the Suspense boundary, so it mounts with
 * the route's real content, after its chunk has resolved; PageTransition above
 * it owns only the exit. The animation is CSS (.pf-route-enter[data-entrance]
 * in adjacent.css) so it stays smooth while the freshly loaded chunk hydrates.
 * On a hard load the prerendered static hero already holds the paint, so the
 * first route mounts in its settled state.
 */
function RouteEnter({ children }: { children: ReactNode }) {
  const reduced = useReducedMotion();
  const [hadStaticHero] = useState(() => document.getElementById("static-hero") !== null);
  const entrance = !(hadStaticHero || reduced);
  useScrollResetOnMount();
  // No enter to play (reduced motion, hard load): tell PageTransition the old
  // page can go now rather than waiting for an animationend that never comes.
  useEffect(() => { if (!entrance) window.dispatchEvent(new Event("pfrouteenter")); }, [entrance]);
  // The destination exists only after its lazy route has mounted. This covers
  // direct hash loads, cross-page hash links, and back/forward navigation.
  useEffect(() => {
    if (window.location.hash.length > 1) scrollToSection(window.location.hash.slice(1));
  }, []);
  return <div className="pf-route-enter" data-entrance={entrance ? "" : undefined}>{children}</div>;
}

const DeferredToaster = lazy(() =>
  import("sonner").then((m) => ({ default: m.Toaster }))
);
const loadMotionFeatures = () =>
  import("./utils/motionFeatures").then((m) => m.default);

function IntentToaster() {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    if (enabled) return;
    const enable = () => setEnabled(true);
    window.addEventListener("pointerdown", enable, { once: true, passive: true });
    window.addEventListener("keydown", enable, { once: true });
    window.addEventListener("focusin", enable, { once: true });
    return () => {
      window.removeEventListener("pointerdown", enable);
      window.removeEventListener("keydown", enable);
      window.removeEventListener("focusin", enable);
    };
  }, [enabled]);

  return enabled ? <Suspense fallback={null}><DeferredToaster position="top-right" richColors closeButton /></Suspense> : null;
}

/** Mounts only after the surrounding Suspense boundary has resolved. */
function RouteReady() {
  useEffect(() => { document.getElementById("static-hero")?.remove(); }, []);
  return null;
}

function PageLoader() {
  // Transparent background: on initial load the prerendered #static-hero must
  // stay visible behind the fallback while the route chunk downloads.
  //
  // Surface-relative tokens, not `neutral-200`/`neutral-600`: this renders on
  // whichever surface the incoming route opens on (ink, for all of them), and
  // DESIGN.md §2 retires cool greys by name. The one element a visitor stares
  // at while waiting was the one painted outside the palette.
  // `surface-ink` flips the tokens; the inline transparent background undoes the
  // one other thing that class does: on initial load the prerendered
  // #static-hero must stay visible behind the fallback while the route chunk
  // downloads, so this must not paint.
  // The loader always renders on the ink canvas now, and the paper tokens
  // resolved to ink-on-ink there — an invisible head on an invisible ring. On
  // the ink surface `border-t-foreground` is ink-text against an ink-border
  // track, which is the contrast this control needs.
  //
  // The spinner is withheld for 150ms. With route chunks prefetched on intent
  // most arrivals resolve inside that window, and a spinner that flashes for
  // two frames reads as slower than no spinner at all.
  useScrollResetOnMount();
  const [show, setShow] = useState(false);
  useEffect(() => {
    const timer = window.setTimeout(() => setShow(true), 150);
    return () => window.clearTimeout(timer);
  }, []);
  return (
    <div className="surface-ink min-h-screen flex items-center justify-center" style={{ backgroundColor: "transparent" }}>
      {/* A dedicated class (adjacent.css): the network's `.rounded-full[class*=border]`
          rule was squaring the Tailwind version to 2px and its head colour resolved
          to the track colour, leaving a headless rotating square. 700ms, not 1s: a
          quicker spinner reads as a quicker load. data-status-motion swaps it for the
          status-breathe pulse under reduced motion. */}
      {show && <div className="pf-route-spinner" data-status-motion="" role="status" aria-label="Loading page" />}
    </div>
  );
}

export default function App() {
  const [currentPage, setCurrentPage] = useState<PageId>(() => {
    // Redirect legacy hash URLs before reading pathname
    redirectLegacyHash();
    return pathToPageId(window.location.pathname);
  });
  // The page type is shared by every article and every edition. A pathname
  // key lets navigation between two slugs mount the new page and crossfade it.
  const [currentPath, setCurrentPath] = useState(() => window.location.pathname);
  useRoutePrefetch();
  useRouteLinks();
  useInPageAnchors();

  useEffect(() => {
    initializeSEO();

    // Hide static SEO fallback content now that React has mounted
    const seoFallback = document.getElementById("seo-fallback");
    if (seoFallback) {
      // The fallback repeats live section ids (commercial-calculator among them).
      // Drop them once React owns the page so the document has one of each.
      seoFallback.querySelectorAll<HTMLElement>("[id]").forEach((el) => el.removeAttribute("id"));
      seoFallback.style.display = "none";
    }

    const handlePopState = () => {
      const page = pathToPageId(window.location.pathname);
      // Every in-app navigation (link interception, navigate(), back/forward)
      // arrives here. Start the chunk now: lazy() would only request it when the
      // new route first renders, and the crossfade (PageTransition) keeps the
      // outgoing page on screen until the incoming one has settled.
      prefetchPage(page);
      setCurrentPage(page);
      setCurrentPath(window.location.pathname);
    };

    window.addEventListener("popstate", handlePopState);
    // A calculator hash can be clicked before its lazy component has mounted.
    const handleHashChange = () => {
      if (window.location.hash.length > 1) scrollToSection(window.location.hash.slice(1));
    };
    window.addEventListener("hashchange", handleHashChange);

    return () => {
      window.removeEventListener("popstate", handlePopState);
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, []);

  const navigate = (page: PageId) => {
    if (page === "article") return; // Articles navigate via navigateToArticle
    if (page === "tools") page = "rentrollfinance"; // suite lives on /rent-roll-finance now
    // The scroll reset is owed, not performed: navigateTo flags it and the
    // incoming page runs it in its own layout effect (useScrollResetOnMount).
    // PageTransition keeps the outgoing page on screen, at its own scroll
    // position, beneath the incoming one until that one has settled.
    navigateTo(page);
  };

  return (
    <ErrorBoundary>
      {/* LazyMotion + domAnimation ships the lightweight motion feature set
          (~half the full bundle) so hydrating the 40+ animated components costs
          less main-thread work. `strict` guarantees every component uses the
          `m` alias (no accidental full-`motion` bundle). domAnimation covers
          all features in use here — variants, whileInView, gestures — and
          the app uses no layout/drag/path animations that would need domMax. */}
      <LazyMotion features={loadMotionFeatures} strict>
        {/* Skip link lives in index.html (outside #root) so it exists pre-hydration
            and persists across SPA navigations — rendering a second one here
            duplicated it in the tab order. */}
        <IntentToaster />
        {/* Suspense sits INSIDE PageTransition, not outside it: the fallback for a
            cold route mounts in the live slot while the outgoing page stays in
            the old slot beneath it, so a first trip to any route keeps the page
            the visitor was reading on screen until the chunk arrives. */}
        <div className="pf-network"><PageTransition pageKey={currentPath}>
          <Suspense fallback={<PageLoader />}>
            <RouteEnter key={currentPath}>
            {(["finance", "services", "advisoryrentroll", "commercial", "property", "insights", "contact", "about", "local"] as const).map(kind => currentPage === kind && <AdvisoryPage key={kind} kind={kind} />)}
            {currentPage === "home" && <PendiumHomePage onNavigate={navigate} />}
            {currentPage === "firsthome" && <FirstHomeBuyerPage onNavigate={navigate} />}
            {currentPage === "downsizing" && <DownsizingPage onNavigate={navigate} />}
            {/* /tools is consolidated into /rent-roll-finance (vercel.json also 308s direct hits) */}
            {currentPage === "tools" && <AdvisoryPage kind="advisoryrentroll" />}
            {currentPage === "blog" && <BlogPage onNavigate={navigate} />}
            {currentPage === "article" && <ArticlePage onNavigate={navigate} />}
            {currentPage === "edition" && <NewsletterEditionPage onNavigate={navigate} />}
            {currentPage === "rentroll" && <RentRollAcquisitionPage onNavigate={navigate} />}
            {currentPage === "rentrollfinance" && <AdvisoryPage kind="advisoryrentroll" />}
            {currentPage === "newsletter" && <NewsletterArchivePage onNavigate={navigate} />}
            {currentPage === "complaints" && <FeedbackComplaintsPage onNavigate={navigate} />}
            {currentPage === "websitepolicy" && <WebsitePolicyPage onNavigate={navigate} />}

            {currentPage === "notfound" && <NotFoundPage onNavigate={navigate} />}
            <RouteReady />
            </RouteEnter>
          </Suspense>
        </PageTransition></div>
      </LazyMotion>
    </ErrorBoundary>
  );
}
